// CheckoutFlow — marketplace checkout: address selection, billing sub-step,
// payment, and order review/submit.
//
// Extracted from MarketplaceEmbed.tsx (Task 5, TD-269 Sub-project D UX
// split). This is the highest-risk extraction in the plan: 12 pieces of
// checkout state and two behaviours with documented bug histories move
// together.
//
// Task 4 parked a seven-call reset (checkoutStep, addressSubStep,
// billingSameAsShipping, billingAddressId, billingAddressDisplay,
// billingError, completedSteps) in the parent's onProceedToCheckout, because
// CheckoutFlow did not exist yet as its own mount point. Now that it does,
// CheckoutFlow mounts fresh every time the parent switches into the
// "checkout" route (the switch-based router unmounts/remounts on route
// change, same as every other extracted view) — so initialising these
// useState calls to the reset values reproduces that seven-call reset
// exactly, and the parent's copy is redundant and has been removed.
//
// TD-290 (b): unchecking "billing same as shipping" clears BOTH
// billingAddressId and billingAddressDisplay — clearing only one
// reintroduces the stale-billing bug.
// TD-290 (d): the aria-live="assertive" wrapper below the billing checkbox
// stays mounted even when billingError is null, so a screen reader reliably
// announces the error the moment it appears rather than missing it because
// the live region mounted together with its content.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "../../embed-shared/cart-context";
import { useAuth } from "../../embed-shared/auth-context";
import { embedApiFetch, ApiError } from "../../embed-shared/api-client";
import {
  idempotencyHeaders,
  idempotencyUserMessage,
  isIdempotencyCodeBug,
} from "../../embed-shared/idempotency-errors";
import { useEmbedTokens, EmbedBanner, EmbedButton, EmbedCheckbox } from "../../embed-shared/primitives";
import type { AddressWire } from "./address-api";
import { CheckoutStepper, type CheckoutStep } from "./CheckoutStepper";
import { detailInsetStyle, useViewportWidth } from "./detail-inset";
import { AddressSelector } from "./AddressSelector";
import { PaymentForm, type PaymentFormData } from "./PaymentForm";
import { OrderReviewPanel } from "./OrderReviewPanel";
import { brandLabel } from "./payment-format";
import { getActivePaymentTerms, type PaymentTermsDocumentWire } from "./payment-terms-api";

export interface CheckoutFlowProps {
  onBackToCart: () => void;
  /** Redesign Task 17: carries the placed order's id (drives the
   *  confirmation's GET /me/orders/{orderId} snapshot read) plus the hero
   *  strings the confirmation renders even if that read fails. */
  onOrderPlaced: (orderId: string, orderNumber: string, email: string) => void;
}

/**
 * Stable account identity read from a customer JWT payload, or null when it
 * cannot be determined.
 *
 * The payload is read UNVERIFIED, deliberately. This value decides one thing
 * only: whether locally-held checkout DISPLAY state may be reused across a
 * token change. It is never an authorization input — the server re-derives
 * CustomerID from the verified token and rejects an address that is not the
 * caller's with 422 (order_service.go's Address.GetByID scoping). So a forged
 * payload can at most make a customer keep or lose their OWN selection; it can
 * never surface another account's data. Claim names mirror what the service
 * mints: authwire/token_service.go dual-emits `sub` (string) and `user_id`
 * (int64).
 */
function accountIdFromToken(token: string | null): string | null {
  if (!token) return null;
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
    const claims = JSON.parse(json) as { user_id?: unknown; sub?: unknown };
    const id = claims.user_id ?? claims.sub;
    return typeof id === "string" || typeof id === "number" ? String(id) : null;
  } catch {
    return null;
  }
}

/**
 * Marks "populated while the account could not be identified". Kept distinct
 * from null ("nothing populated yet") so an unidentifiable token fails CLOSED:
 * it clears rather than silently reusing state that might belong to someone
 * else.
 */
const UNKNOWN_ACCOUNT = "unidentified-account";

export function CheckoutFlow({ onBackToCart, onOrderPlaced }: CheckoutFlowProps) {
  const t = useEmbedTokens();
  const viewportWidth = useViewportWidth();
  const { state: cartState, clearCart, fetchCart } = useCart();
  const cart = cartState.cart;

  // Checkout flow state — shipping_address_id (not inline address per TD-231).
  // Initialised to the same values Task 4's onProceedToCheckout reset
  // produced (see file header) — CheckoutFlow always mounts on a clean slate.
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>("address");
  const [completedSteps, setCompletedSteps] = useState<CheckoutStep[]>([]);
  const [shippingAddressId, setShippingAddressId] = useState<number | null>(null);
  const [shippingAddressDisplay, setShippingAddressDisplay] = useState<AddressWire | null>(null);
  // Billing sub-step (TD-237 #5): shipping selection advances to an in-step
  // billing sub-step. "Same as shipping" (default) resolves billing to the
  // shipping id at submit; unchecking requires a distinct billing address.
  const [addressSubStep, setAddressSubStep] = useState<"shipping" | "billing">("shipping");
  const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(true);
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [billingAddressDisplay, setBillingAddressDisplay] = useState<AddressWire | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentFormData | null>(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // Task 10, Task-9 review finding (b): set alongside checkoutError when the
  // order POST was refused by the checkout store gate (422
  // STORE_REQUIRED_FOR_RECURRING) — the banner copy then names the offending
  // line and this flag renders the one actionable remediation ("Return to
  // your cart", where the store control force-shows via CartView's
  // live-resolution arm).
  const [storeGateReturn, setStoreGateReturn] = useState(false);
  // C3 Task 10: set when the order POST was answered 409 QUOTE_CHANGED. The
  // banner copy is derived at render from this plus the cart load status, so a
  // FAILED refetch never claims an updated order is shown below.
  const [quoteChanged, setQuoteChanged] = useState(false);
  // Payment terms (§4). `null` documents means "not fetched yet" — the gate
  // renders a loading state rather than an empty, tickable box.
  const [termsDocuments, setTermsDocuments] = useState<PaymentTermsDocumentWire[] | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  // TD-458 (409 TERMS_CHANGED), extended Item 4 (409 TERMS_VARIANT_CHANGED,
  // fix wave 2026-09-15): why the customer was returned to the payment step
  // — see recoverToPaymentStep below, the shared bounce both errors drive.
  // Cleared when a fresh acceptance is collected — until then it is the
  // only explanation on screen for the bounce.
  const [termsChangedNotice, setTermsChangedNotice] = useState<string | null>(null);

  // -- The geo authority, as this client can see it (§1.1, §1.3) -------------
  //
  // The billing address is the authority — not shipping, not the account. When
  // "same as shipping" is ticked the billing reference IS the shipping row, so
  // the country is read from that row regardless of its own `address_type`
  // discriminator: an order's billing address is very often a row typed
  // 'shipping', and filtering on the type instead of following the reference is
  // the §1.4 trap that silently routes international customers to ACH.
  //
  // This is a PROJECTION and never the guard (§1.3). The authoritative
  // evaluation happens server-side at order creation, against the country
  // snapshotted onto the order. Its only job here is to keep the customer from
  // being shown a payment form they cannot use.
  const billingAddress = billingSameAsShipping ? shippingAddressDisplay : billingAddressDisplay;
  const billingCountryCode = billingAddress?.country_code ?? null;
  // ONE shared derivation of the billing row's ID — the capture call and the
  // submit body must carry the SAME geo authority, or the server could derive
  // a different method at order time than the form the customer filled. The
  // capture wire requires it outright (TD-461: its absence 422'd every
  // capture).
  const resolvedBillingAddressId = billingSameAsShipping ? shippingAddressId : billingAddressId;

  // -- Account-scoping of checkout display state -----------------------------
  //
  // MarketplaceEmbed's preserveState keeps this component MOUNTED through the
  // auth gate — that is Task 13's whole point, so a 401 mid-checkout does not
  // discard what the customer typed. The consequence is that
  // shippingAddressDisplay (a full address: street, city, postal code) and the
  // tokenized paymentData outlive the token that produced them.
  // SignInOverlay covers them opaquely WHILE gated, but when a DIFFERENT
  // account signs in the overlay unmounts and the previous customer's address
  // is on screen in OrderReviewPanel. The address IDs are safe — the server
  // scopes them to its own derived CustomerID — but the DISPLAY is client-only
  // state no server check touches.
  //
  // The discriminator is the ACCOUNT, not the token string: re-authenticating
  // as the same customer always mints a FRESH token, so clearing on any string
  // change would clear on every ordinary gate recovery — undoing Task 13 and
  // stranding the customer on the review step with no address and a "Shipping
  // address is required" error on submit.
  const { token } = useAuth();
  const accountId = useMemo(() => accountIdFromToken(token), [token]);
  /** Account under which the address/payment display state was entered; null
   *  while nothing has been entered. */
  const populatedByRef = useRef<string | null>(null);
  /** Bumped to force a fresh PaymentForm — see the account-change effect. */
  const [paymentFormEpoch, setPaymentFormEpoch] = useState(0);
  function rememberPopulatingAccount() {
    populatedByRef.current = accountId ?? UNKNOWN_ACCOUNT;
  }

  useEffect(() => {
    // Nothing entered yet — nothing to protect.
    if (populatedByRef.current === null) return;
    // Token cleared (the gate engaging). Content is inert behind the overlay
    // and the same customer usually comes back, so PRESERVE — this is exactly
    // the case Task 13 exists for.
    if (!token) return;
    // Provably the same customer: keep what they entered.
    if (accountId !== null && accountId === populatedByRef.current) return;

    populatedByRef.current = null;
    // Remounts PaymentForm, discarding the previous customer's typed fields AND
    // their terms acceptance. The acceptance is the reason this is not
    // cosmetic: a tick that survived an account change would record customer B
    // as having accepted text only customer A was ever shown. Bumping a key is
    // used rather than resetting checkoutStep because resetting the step would
    // unmount PaymentForm on every ordinary gate recovery too, which is exactly
    // what the branch above preserves.
    setPaymentFormEpoch((n) => n + 1);
    setShippingAddressId(null);
    setShippingAddressDisplay(null);
    setBillingAddressId(null);
    setBillingAddressDisplay(null);
    setBillingSameAsShipping(true);
    setBillingError(null);
    // Not merely cosmetic: handleCheckout submits
    // paymentData.payment_instrument_id, so a carried-over payment method
    // would charge the previous customer's instrument for this order.
    setPaymentData(null);
    setCheckoutError(null);
    setStoreGateReturn(false);
    setQuoteChanged(false);
    setTermsChangedNotice(null);
    // checkoutStep/completedSteps are deliberately NOT reset. They are
    // navigation, not account data, and resetting checkoutStep would unmount
    // PaymentForm — destroying the in-progress field values that Task 13's
    // preservation guarantee is pinned on.
  }, [token, accountId]);

  // -- Payment terms, fetched fail-CLOSED (§4.5) ----------------------------
  //
  // ⚠️ Item 2 fix (R2 catalog display design, fix wave 2026-09-15, operator
  // ruling): this used to derive its own opinion of the variant here
  // (`deriveTermsVariant(cartLines)`, payment-terms.ts) and pass it to
  // getActivePaymentTerms as a `?variant=` hint that SELECTED which
  // documents the server returned — the client's cart-line snapshot could
  // disagree with what the server would itself resolve, showing the wrong
  // authorization-to-charge text on a legal/money surface. STOPPED: this
  // component no longer computes a variant at all. getActivePaymentTerms()
  // now takes no argument, and Item 3's companion server fix
  // (customer_handler_payment_terms.go) makes a supplied `variant` select
  // NOTHING — the variant (§4.2: "evaluated server-side from the
  // order's lines") is ALWAYS derived by the server from the cart. Which
  // variant applied is read back from the RESPONSE itself
  // (`PaymentTermsDocumentWire.variant` per document), never guessed ahead
  // of the fetch — this component has no other use for it, so nothing
  // extracts or stores it separately; the returned documents ARE the
  // server's answer, displayed as-is.
  //
  // ⚠️ AMENDED (fix wave 2026-09-16, verified gate finding 1): IGNORED, not
  // rejected. ORIGINAL: "Item 3's companion server fix
  // (customer_handler_payment_terms.go) REJECTS a supplied `variant`
  // outright (400)". CURRENT: that 400 was itself reverted in the same fix
  // wave and the handler now IGNORES the parameter. The reason is a
  // compatibility one and it matters here: this embed is a SEPARATE CDN
  // artifact from the ecom-api Lambda and is never deployed atomically with
  // it, so a browser still holding a pre-Item-2 bundle DOES send `?variant=`
  // — and the 400 blocked its checkout outright until the bundle was
  // republished and its cache expired. Do NOT "restore" the rejection to
  // match this comment's old wording; that reproduces the outage. The
  // authoritative statement is `HandleMyPaymentTerms`'s own doc block.
  //
  // Fetched when the payment step is reached, not on mount: the step is where
  // the terms are displayed and the customer may never get there.
  const onPaymentStep = checkoutStep === "payment";

  useEffect(() => {
    if (!onPaymentStep) return;
    let cancelled = false;
    setTermsDocuments(null);
    setTermsError(null);
    (async () => {
      try {
        const documents = await getActivePaymentTerms();
        if (!cancelled) setTermsDocuments(documents);
      } catch (err) {
        if (cancelled) return;
        // Deliberate production error-path observability.
        console.error("[checkout] payment terms fetch failed", err);
        // No fallback text, by design. The public-settings route this embed
        // uses elsewhere is fail-open and serves defaults on error; that is
        // right for a hero headline and wrong for an authorization to charge,
        // because a checkout that proceeds on default terms records an
        // acceptance of nothing (§4.5).
        setTermsError(
          "We could not load the payment terms, so this order cannot be placed right now. Please try again shortly.",
        );
      }
    })();
    return () => { cancelled = true; };
  }, [onPaymentStep, paymentFormEpoch]);

  // Shared guided recovery for the two 409s that mean "the payment step must
  // be redone": TD-458's TERMS_CHANGED and the Item 4 addition,
  // TERMS_VARIANT_CHANGED (R2 catalog display design, fix wave 2026-09-15).
  // Same mechanics for both — discard the stale submission (its
  // accepted_terms_ids echo a set that no longer applies; a kept paymentData
  // would let a stepper jump re-POST it), bounce to payment (leaving
  // "payment" out of completedSteps makes Review unreachable again), and let
  // the onPaymentStep effect above refetch + PaymentForm remount the gate
  // UNTICKED (§4.5, fail-closed) — only the notice text differs, because the
  // two errors mean different things to the customer (a document TEXT
  // revision vs. the cart's billing SHAPE changing).
  const recoverToPaymentStep = useCallback((message: string) => {
    setPaymentData(null);
    setTermsDocuments(null);
    setCompletedSteps((prev) => prev.filter((s) => s !== "payment"));
    setTermsChangedNotice(message);
    setCheckoutStep("payment");
  }, []);

  // Task 10, finding (b): converts the checkout store gate's raw server
  // message into a NAMED, actionable remediation. The Go error names the
  // product by id in both of its phrasings ("product <uuid> bills a
  // recurring component…" / "…is associated with a store that no longer
  // exists"), so the id is parseable; the DISPLAY name prefers the cart
  // wire's product_name and falls back to the public catalog page (the same
  // lookup CartView runs for cart presentation — no new surface, no auth).
  // Every degradation lands on copy that still names the ACTION precisely.
  const resolveStoreGateRemediation = useCallback(
    async (serverMessage: string) => {
      const productId =
        /product ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(
          serverMessage,
        )?.[1] ?? null;
      let name: string | null = null;
      if (productId) {
        name =
          cart?.lines.find((l) => l.product_id === productId && l.product_name)?.product_name ??
          null;
        if (name == null) {
          // Cosmetic best-effort: a failed lookup degrades to the neutral
          // copy below; the remediation does not depend on it.
          try {
            const products = await embedApiFetch<
              Array<{ id: string; name?: string }>
            >("/api/v1/marketplace/catalog", { query: { limit: "50" } });
            if (Array.isArray(products)) {
              name = products.find((p) => p.id === productId)?.name ?? null;
            }
          } catch {
            // Neutral fallback below.
          }
        }
      }
      const subject = name ?? "An item in your cart";
      setCheckoutError(
        `${subject} now bills a recurring component and needs a store before checkout. Return to your cart to pick a store for it, or remove the line.`,
      );
      setStoreGateReturn(true);
    },
    [cart],
  );

  const handleCheckout = useCallback(async () => {
    if (!shippingAddressId) {
      setCheckoutError("Shipping address is required");
      return;
    }
    if (!paymentData) {
      setCheckoutError("Payment method is required");
      return;
    }
    setCheckoutSubmitting(true);
    setCheckoutError(null);
    setStoreGateReturn(false);
    setQuoteChanged(false);
    try {
      // Wire shape (§39): the Go handler takes { shipping_address_id,
      // billing_address_id, payment_method (advisory), payment_instrument_id,
      // accepted_terms_ids, notes }. No inline shipping_address (TD-231), and
      // no payment_token — the legacy field is accepted-and-IGNORED server-side
      // and was removed when the wire was aligned (TD-461).
      // Wire keys mirror checkoutResp (customer_handler.go): order_id,
      // order_number, confirmation_email — order_id drives the confirmation
      // view's own snapshot fetch (Task 17); no client checkout state carries
      // into that view.
      const result = await embedApiFetch<{ order_id: string; order_number: string; confirmation_email: string }>(
        "/api/v1/marketplace/me/orders",
        {
          method: "POST",
          body: {
            shipping_address_id: shippingAddressId,
            // Approach A: "same as shipping" resolves billing to the shipping
            // id at submit; otherwise send the distinct billing selection.
            // Same expression the capture call used — see resolvedBillingAddressId.
            billing_address_id: resolvedBillingAddressId,
            payment_method: paymentData.payment_method,
            payment_instrument_id: paymentData.payment_instrument_id,
            // §4.4: the SET of terms revisions the customer accepted, never a
            // boolean — that is what makes the exact text reconstructable
            // later. The server records acceptance from the revisions IT
            // resolved as active and rejects a stale set, so these ids are an
            // echo of what was shown, not a claim the server takes on trust.
            // The Go decode's own field name (TD-456); the server compares
            // this echo against its resolved active set in the checkout TX.
            accepted_terms_ids: paymentData.accepted_terms_ids,
            notes: null,
          },
          headers: idempotencyHeaders(),
        },
      );
      clearCart();
      if (result) {
        onOrderPlaced(result.order_id, result.order_number, result.confirmation_email);
      }
    } catch (err) {
      // Surface idempotency-specific and payment-specific errors with user-friendly messages.
      if (err instanceof ApiError) {
        if (isIdempotencyCodeBug(err.code)) {
          console.warn("[checkout] missing Idempotency-Key — code bug", err.code);
        }
        // Payment-specific error codes from the backend (C5).
        if (err.code === "PAYMENT_UNAVAILABLE") {
          setCheckoutError("Payments are temporarily unavailable. Please try again later.");
        } else if (err.code === "PAYMENT_DECLINED") {
          setCheckoutError("Your payment was declined. Please check your details or use a different method.");
        } else if (err.code === "INVALID_ADDRESS") {
          // Backend rejected a shipping/billing id (not owned or deleted — the
          // Task 1-4 ownership guard maps to 422 INVALID_ADDRESS).
          setCheckoutError("The selected address is no longer available. Please choose another.");
        } else if (err.code === "STORE_REQUIRED_FOR_RECURRING") {
          // Task 10, Task-9 review finding (b): a line whose product GAINED a
          // recurring cell after it was added carries a stale one-time
          // snapshot on the cart wire, so the cart showed no store control —
          // and this LIVE gate (Task 8) just refused the order. The raw
          // server message carries an unparsable product UUID and no action;
          // name the offending LINE by its display name (cart wire first,
          // then the public catalog — the same lookup CartView uses) and
          // offer the one remediation that exists: back to the cart, where
          // the store control force-shows for exactly these stale lines
          // (CartView's live-resolution OR-arm).
          void resolveStoreGateRemediation(err.message);
        } else if (err.code === "TERMS_CHANGED") {
          // TD-458: the accepted-terms set is stale — most plausibly an
          // operator activated a new document REVISION mid-checkout (the TEXT
          // changed; which document applies did not). Guided recovery, not a
          // dead end — see recoverToPaymentStep for the shared mechanics.
          recoverToPaymentStep(
            "The payment terms were updated while you were checking out. Please review the current terms and accept them to place your order.",
          );
        } else if (err.code === "TERMS_VARIANT_CHANGED") {
          // Item 4 fix (R2 catalog display design, fix wave 2026-09-15): the
          // cart's one-time/recurring VARIANT itself changed between this
          // customer's GET /me/payment-terms and this checkout — most
          // plausibly a catalog/price-cell edit landing in that narrow
          // window (order_service.go's mapServiceError: checked BEFORE
          // TERMS_CHANGED, since it also satisfies that sentinel). Items 2
          // and 3 of this same fix wave NARROW this window (the client no
          // longer supplies a variant guess the server has to trust, and the
          // server itself now derives it live for BOTH the GET and
          // checkout) but cannot ELIMINATE it — a catalog edit between the
          // two calls remains possible by design. Before this branch existed
          // this error fell to the generic `else` below: the raw server
          // message told the customer to "review and accept the current
          // terms" while nothing returned them to the payment step to do
          // so, and because the client re-derived the identical (now-wrong)
          // hint on every retry, that was a DEAD END, not a converging loop.
          // DISTINCT copy from TERMS_CHANGED: that means the document TEXT
          // was revised; this means the ORDER'S BILLING SHAPE changed, so
          // the documents shown no longer describe this order at all — not
          // merely an outdated revision of the right ones.
          recoverToPaymentStep(
            "This order's billing changed (one-time vs. recurring) while you were checking out. Please review and accept the current payment terms to place your order.",
          );
        } else if (err.code === "QUOTE_CHANGED") {
          // C3 Task 10: the cart moved under the customer between the review
          // they saw and this submit (409; every checkout can receive it, this
          // embed's included, since it sends no fingerprint). Refresh the cart
          // wire so OrderReviewPanel re-renders the CURRENT lines and totals,
          // stay on review, and say so. NEVER resubmit here: the order is
          // placed only by the customer's next confirm (a fresh key per submit,
          // operator ruling Q8). details.quote is deliberately not rendered —
          // the cart wire stays the embed's one display source. An emptied
          // cart answers 422 CART_EMPTY instead and takes the generic branch.
          setQuoteChanged(true);
          await fetchCart();
        } else {
          const userMsg = idempotencyUserMessage(err.code);
          setCheckoutError(userMsg ?? err.message);
        }
      } else {
        setCheckoutError("Checkout failed. Please try again.");
      }
    } finally {
      setCheckoutSubmitting(false);
    }
  }, [shippingAddressId, resolvedBillingAddressId, paymentData, clearCart, fetchCart, onOrderPlaced, recoverToPaymentStep, resolveStoreGateRemediation]);

  // Brand and last four only — never a full number, in the summary, an error,
  // or a log line (§3.8).
  const paymentSummary = useMemo(() => {
    if (!paymentData) return undefined;
    if (paymentData.payment_method === "card") {
      const brand = paymentData.brand ? brandLabel(paymentData.brand) : "Card";
      return `${brand} ending in ${paymentData.last4 || "****"}`;
    }
    // F23: the rail's acronym is retired from customer-facing copy — the
    // account type is always business checking now, so the fallback mirrors
    // the locked value.
    return `Bank transfer ${paymentData.accountType ?? "checking"} ending in ${paymentData.last4 || "****"}`;
  }, [paymentData]);

  return (
    <div style={detailInsetStyle(t, viewportWidth)}>
      <CheckoutStepper
        currentStep={checkoutStep}
        completedSteps={completedSteps}
        onStepClick={(step) => setCheckoutStep(step)}
      />
      <div style={{ marginTop: t.spacing.lg }}>
        {checkoutStep === "address" && addressSubStep === "shipping" && (
          <AddressSelector
            variant="shipping"
            onAddressSelected={(addrId, address) => {
              rememberPopulatingAccount();
              setShippingAddressId(addrId);
              // Use the address object passed directly from AddressSelector
              // (either from its loaded list or from the createAddress response).
              // No re-fetch needed — avoids replica-lag race (project rule #3).
              setShippingAddressDisplay(address);
              // Advance to the billing sub-step (not straight to payment).
              setAddressSubStep("billing");
            }}
            onBack={onBackToCart}
          />
        )}
        {checkoutStep === "address" && addressSubStep === "billing" && (
          <div>
            <EmbedCheckbox
              label="Billing address same as shipping"
              checked={billingSameAsShipping}
              onChange={(e) => {
                const checked = e.target.checked;
                setBillingSameAsShipping(checked);
                setBillingError(null);
                if (!checked) {
                  // TD-290 (b): keep this behavior comment — unchecking clears any prior billing pre-selection.
                  setBillingAddressId(null);
                  setBillingAddressDisplay(null);
                }
              }}
            />

            {!billingSameAsShipping && (
              <div style={{ marginTop: t.spacing.md }}>
                <AddressSelector
                  variant="billing"
                  onAddressSelected={(addrId, address) => {
                    rememberPopulatingAccount();
                    setBillingAddressId(addrId);
                    setBillingAddressDisplay(address);
                    setBillingError(null);
                  }}
                  onBack={() => setAddressSubStep("shipping")}
                />
              </div>
            )}

            {/* TD-290 (d): the aria-live region stays mounted (empty when
                there is no error) so screen readers reliably announce the
                error the moment it appears, rather than mounting the live
                region together with its content. */}
            <div style={{ marginTop: billingError ? t.spacing.sm : 0 }} aria-live="assertive">
              {billingError && <EmbedBanner tone="error">{billingError}</EmbedBanner>}
            </div>

            <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
              <EmbedButton
                type="button"
                variant="secondary"
                onClick={() => setAddressSubStep("shipping")}
              >
                Back
              </EmbedButton>
              <EmbedButton
                type="button"
                variant="primary"
                fullWidth
                onClick={() => {
                  if (!billingSameAsShipping && !billingAddressId) {
                    setBillingError("Billing address is required");
                    return;
                  }
                  setCompletedSteps((prev) => [...prev.filter((s) => s !== "address"), "address"]);
                  setCheckoutStep("payment");
                }}
              >
                Continue to payment
              </EmbedButton>
            </div>
          </div>
        )}
        {checkoutStep === "payment" && (
          <>
            {/* TD-458 (409 TERMS_CHANGED) / Item 4 (409 TERMS_VARIANT_CHANGED):
                shown only after one of the two guided-recovery bounces —
                the customer did nothing wrong and was moved a step backward,
                so an explanation is required either way. role=alert
                (EmbedBanner's error tone) announces it, matching the
                billing-error pattern. */}
            {termsChangedNotice && (
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedBanner tone="error">{termsChangedNotice}</EmbedBanner>
              </div>
            )}
            <PaymentForm
              key={paymentFormEpoch}
              billingCountryCode={billingCountryCode}
              billingAddressId={resolvedBillingAddressId}
              termsDocuments={termsDocuments}
              termsError={termsError}
              onSubmit={(data) => {
                rememberPopulatingAccount();
                setPaymentData(data);
                // A fresh acceptance has been collected against the current
                // revisions — the explanation for the bounce is spent.
                setTermsChangedNotice(null);
                setCompletedSteps((prev) => [...prev.filter((s) => s !== "payment"), "payment"]);
                setCheckoutStep("review");
              }}
              onBack={() => setCheckoutStep("address")}
            />
          </>
        )}
        {/* `cart &&` alone is no longer a "cart is loaded AND has something in
            it" test. cart-context's CLEAR now resolves to
            {status:"ready", cart: EMPTY_CART} rather than a null cart, so
            after handleCheckout's clearCart() the cart stays TRUTHY. If
            onOrderPlaced did not fire — the order POST resolved without a
            `data` envelope, so `result` was null (api-client.ts) and nothing
            routed away — this panel stayed mounted over an empty cart,
            offering a live "Place order" button that would POST a SECOND
            order under a fresh Idempotency-Key. Requiring lines, exactly as
            CartView does, means the panel cannot outlive the cart it
            summarises. */}
        {checkoutStep === "review" && cart && cart.lines.length > 0 && (
          <>
            {quoteChanged && (
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedBanner tone="error">
                  {cartState.status === "error"
                    ? "We could not load your updated cart. Reload it before placing your order."
                    : cartState.status === "loading"
                      ? "The items or prices in your cart changed while you were checking out. Loading your updated cart…"
                      : "The items or prices in your cart changed while you were checking out. Please review the updated order below before placing it again."}
                </EmbedBanner>
              </div>
            )}
            {checkoutError && (
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedBanner tone="error">{checkoutError}</EmbedBanner>
                {/* Task 10 finding (b): the store-gate refusal is actionable
                    ONLY on the cart surface — the remediation button takes
                    the customer there (the control force-shows for the
                    stale-snapshot line it names). */}
                {storeGateReturn && (
                  <div style={{ marginTop: t.spacing.xs }}>
                    <EmbedButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={onBackToCart}
                    >
                      Return to your cart
                    </EmbedButton>
                  </div>
                )}
              </div>
            )}
            {/* C3 Task 10: a failed (or in-flight) cart refetch after a
                QUOTE_CHANGED means the lines below may be stale, so the
                confirm control is disabled until the cart loads. */}
            {quoteChanged && cartState.status === "error" && (
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedBanner tone="error">
                  {cartState.error ?? "We could not refresh your cart."}
                </EmbedBanner>
                <div style={{ marginTop: t.spacing.xs }}>
                  <EmbedButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void fetchCart()}
                  >
                    Reload cart
                  </EmbedButton>
                </div>
              </div>
            )}
            <OrderReviewPanel
              lines={cart.lines}
              subtotal={cart.subtotal}
              taxEstimate={cart.tax_estimate}
              promoDiscount={cart.promo_discount}
              shipping={cart.shipping}
              total={cart.total}
              promoCode={cart.promo_code}
              shippingAddress={shippingAddressDisplay}
              billingAddress={billingSameAsShipping ? shippingAddressDisplay : billingAddressDisplay}
              paymentSummary={paymentSummary}
              onConfirm={handleCheckout}
              onBack={() => setCheckoutStep("payment")}
              confirming={checkoutSubmitting}
              confirmDisabled={quoteChanged && (cartState.status === "error" || cartState.status === "loading")}
            />
          </>
        )}
      </div>
    </div>
  );
}
